import { useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Loader2, AlertCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

const EditRSVPModal = ({ rsvp, event, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    rsvp_status: rsvp.rsvp_status,
    message: rsvp.message || '',
    adults_male: rsvp.adults_male || 0,
    adults_female: rsvp.adults_female || 0,
    kids_male: rsvp.kids_male || 0,
    kids_female: rsvp.kids_female || 0
  });

  // Always use 3 as max edits (handles both old and new records)
  const maxEdits = 3;
  const editsRemaining = maxEdits - rsvp.edit_count;

  // Calculate time information
  const getTimeInfo = () => {
    if (!event) return null;
    
    const eventDateTime = new Date(`${event.date}T${event.time}`);
    const now = new Date();
    const hoursUntilEvent = (eventDateTime - now) / (1000 * 60 * 60);
    const editDeadline = new Date(eventDateTime.getTime() - (1 * 60 * 60 * 1000)); // 1 hour before
    
    return {
      eventDateTime,
      editDeadline,
      hoursUntilEvent,
      canEdit: hoursUntilEvent > 1,
      eventDateStr: eventDateTime.toLocaleDateString('en-IN', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      eventTimeStr: eventDateTime.toLocaleTimeString('en-IN', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      deadlineStr: editDeadline.toLocaleString('en-IN', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const timeInfo = getTimeInfo();

  const handleUpdate = async () => {
    if (!formData.rsvp_status) {
      toast.error('Please select an RSVP status');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/rsvps/${rsvp.id}`,
        {
          rsvp_status: formData.rsvp_status,
          message: formData.message || undefined,
          adults_male: formData.adults_male,
          adults_female: formData.adults_female,
          kids_male: formData.kids_male,
          kids_female: formData.kids_female
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        toast.success('RSVP updated successfully!');
        onSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update RSVP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-rsvp-modal">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-2xl font-bold">Edit Your RSVP</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 md:space-y-6 py-2 md:py-4">
          {/* 12-Hour Restriction Info with Dynamic Time */}
          {timeInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-semibold mb-2">
                  Time Restriction
                </p>
                <div className="space-y-1 text-xs text-blue-700">
                  <p>
                    <strong>Event Date & Time:</strong><br />
                    {timeInfo.eventDateStr} at {timeInfo.eventTimeStr}
                  </p>
                  <p>
                    <strong>Edit Deadline:</strong><br />
                    {timeInfo.deadlineStr}
                  </p>
                  <p className="mt-2 text-blue-800 font-medium">
                    {timeInfo.canEdit ? (
                      <>✓ You can edit your RSVP until 1 hour before the event</>
                    ) : (
                      <>✗ Editing not allowed (within 1 hour of event)</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Edit Limit Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800 font-semibold">
                {editsRemaining} edit{editsRemaining !== 1 ? 's' : ''} remaining
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                You can edit your RSVP {maxEdits} times total after your initial response
              </p>
            </div>
          </div>

          {/* RSVP Status Selection */}
          <div className="space-y-3 md:space-y-4">
            <Label className="text-sm md:text-base">Update your attendance status *</Label>
            <RadioGroup
              value={formData.rsvp_status}
              onValueChange={(value) => setFormData({ ...formData, rsvp_status: value })}
              data-testid="edit-rsvp-status-selector"
            >
              <div className="flex items-center space-x-2 border-2 border-green-100 p-2 md:p-3 rounded-lg bg-green-50">
                <RadioGroupItem value="yes" id="edit-yes" data-testid="edit-rsvp-yes-option" />
                <Label htmlFor="edit-yes" className="flex-1 cursor-pointer text-sm md:text-base">
                  <span className="font-medium text-green-700">✓ Yes, Coming</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2 border-2 border-red-100 p-2 md:p-3 rounded-lg bg-red-50">
                <RadioGroupItem value="no" id="edit-no" data-testid="edit-rsvp-no-option" />
                <Label htmlFor="edit-no" className="flex-1 cursor-pointer text-sm md:text-base">
                  <span className="font-medium text-red-700">✗ No, Not coming</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2 border-2 border-yellow-100 p-2 md:p-3 rounded-lg bg-yellow-50">
                <RadioGroupItem value="maybe" id="edit-maybe" data-testid="edit-rsvp-maybe-option" />
                <Label htmlFor="edit-maybe" className="flex-1 cursor-pointer text-sm md:text-base">
                  <span className="font-medium text-yellow-700">? Maybe</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Guest Count Section */}
          <div className="space-y-3 md:space-y-4 border-2 border-blue-100 rounded-lg p-3 md:p-4 bg-blue-50">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              <Label className="text-sm md:text-base font-semibold text-blue-900">Number of Guests</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {/* Adults Male */}
              <div className="space-y-1">
                <Label htmlFor="adults-male" className="text-xs md:text-sm text-gray-700">
                  Adults (Male)
                </Label>
                <Input
                  id="adults-male"
                  type="number"
                  min="0"
                  value={formData.adults_male}
                  onChange={(e) => setFormData({ ...formData, adults_male: parseInt(e.target.value) || 0 })}
                  className="text-sm md:text-base"
                  data-testid="edit-adults-male-input"
                />
              </div>

              {/* Adults Female */}
              <div className="space-y-1">
                <Label htmlFor="adults-female" className="text-xs md:text-sm text-gray-700">
                  Adults (Female)
                </Label>
                <Input
                  id="adults-female"
                  type="number"
                  min="0"
                  value={formData.adults_female}
                  onChange={(e) => setFormData({ ...formData, adults_female: parseInt(e.target.value) || 0 })}
                  className="text-sm md:text-base"
                  data-testid="edit-adults-female-input"
                />
              </div>

              {/* Kids Male */}
              <div className="space-y-1">
                <Label htmlFor="kids-male" className="text-xs md:text-sm text-gray-700">
                  Kids (Male)
                </Label>
                <Input
                  id="kids-male"
                  type="number"
                  min="0"
                  value={formData.kids_male}
                  onChange={(e) => setFormData({ ...formData, kids_male: parseInt(e.target.value) || 0 })}
                  className="text-sm md:text-base"
                  data-testid="edit-kids-male-input"
                />
              </div>

              {/* Kids Female */}
              <div className="space-y-1">
                <Label htmlFor="kids-female" className="text-xs md:text-sm text-gray-700">
                  Kids (Female)
                </Label>
                <Input
                  id="kids-female"
                  type="number"
                  min="0"
                  value={formData.kids_female}
                  onChange={(e) => setFormData({ ...formData, kids_female: parseInt(e.target.value) || 0 })}
                  className="text-sm md:text-base"
                  data-testid="edit-kids-female-input"
                />
              </div>
            </div>

            {/* Total Guests Display */}
            <div className="bg-white rounded-lg p-2 md:p-3 border border-blue-200">
              <p className="text-xs md:text-sm text-gray-700">
                <strong>Total Guests:</strong>{' '}
                <span className="text-lg md:text-xl font-bold text-blue-600">
                  {formData.adults_male + formData.adults_female + formData.kids_male + formData.kids_female}
                </span>
              </p>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="edit-message" className="text-sm md:text-base">Update Message (Optional)</Label>
            <Textarea
              id="edit-message"
              placeholder="Update your message to the host..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              className="text-sm md:text-base"
              data-testid="edit-rsvp-message-input"
            />
          </div>

          {/* Note about contributions */}
          {rsvp.contribution_amount && rsvp.contribution_amount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-3">
              <p className="text-xs md:text-sm text-blue-800">
                <strong>Note:</strong> Your contribution of ₹{rsvp.contribution_amount.toLocaleString()} cannot be changed. 
                Only your attendance status and message can be updated.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <Button
              variant="outline"
              className="flex-1 text-xs md:text-sm"
              onClick={onClose}
              data-testid="edit-rsvp-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-contribo-navy to-contribo-gold text-white text-xs md:text-sm"
              onClick={handleUpdate}
              disabled={loading}
              data-testid="edit-rsvp-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update RSVP'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditRSVPModal;
